<template>
  <div
    class="modal fade"
    id="customerCreateModal"
    tabindex="-1"
    aria-labelledby="customerCreateModalLabel"
    aria-hidden="true"
  >
      <div class="modal-dialog modal-dialog-centered modal-lg">
      <div class="modal-content border-0 shadow">
        <div class="modal-header border-bottom-dashed">
          <div>
            <span class="modal-chip">{{ customer ? $t("customer_page.edit_customer_chip") : $t("customer_page.new_customer") }}</span>
            <h5 class="modal-title fw-bold mt-3 mb-1" id="customerCreateModalLabel">
              {{ customer ? $t("customer_page.update_customer") : $t("customer_page.create_customer") }}
            </h5>
            <p class="text-muted mb-0">
              {{ $t("customer_page.modal_description") }}
            </p>
          </div>
          <button
            type="button"
            class="btn-close shadow-none"
            data-bs-dismiss="modal"
            :aria-label="$t('common.close')"
          ></button>
        </div>

        <div class="modal-body p-4 p-xl-40">
          <form @submit.prevent="handleSubmit">
            <div class="row g-3">
              <div class="col-md-6">
                <div class="field-header">
                  <label class="form-label fw-semibold text-title mb-0">{{ $t("customer_page.customer_code") }}</label>
                  <span class="field-caption">{{ $t("customer_page.customer_code_caption") }}</span>
                </div>
                <div class="reference-input-wrap">
                  <input
                    v-model="formData.code"
                    type="text"
                    class="form-control modal-input"
                    :class="{ 'is-invalid': fieldErrors.code }"
                    :placeholder="$t('customer_page.customer_code_placeholder')"
                    required
                  />
                  <button
                    v-if="!customer"
                    type="button"
                    class="btn btn-light generate-code-btn"
                    :disabled="loading"
                    @click="generateCustomerCode"
                  >
                    <i class="ri-refresh-line me-1"></i>
                    {{ $t("customer_page.generate") }}
                  </button>
                </div>
                <div v-if="fieldErrors.code" class="invalid-feedback">{{ fieldErrors.code }}</div>
              </div>

              <div class="col-md-6">
                <div class="field-header">
                  <label class="form-label fw-semibold text-title mb-0">{{ $t("customer_page.customer_name") }}</label>
                  <span class="field-caption">{{ $t("customer_page.customer_name_caption") }}</span>
                </div>
                <input
                  v-model="formData.name"
                  type="text"
                  class="form-control modal-input"
                  :class="{ 'is-invalid': fieldErrors.name }"
                  :placeholder="$t('customer_page.customer_name_placeholder')"
                  required
                />
                <div v-if="fieldErrors.name" class="invalid-feedback">{{ fieldErrors.name }}</div>
              </div>

              <div class="col-md-6">
                <div class="field-header">
                  <label class="form-label fw-semibold text-title mb-0">{{ $t("customer_page.email") }}</label>
                  <span class="field-caption">{{ $t("customer_page.email_caption") }}</span>
                </div>
                <input
                  v-model="formData.email"
                  type="email"
                  class="form-control modal-input"
                  :class="{ 'is-invalid': fieldErrors.email }"
                  :placeholder="$t('customer_page.email_placeholder')"
                />
                <div v-if="fieldErrors.email" class="invalid-feedback">{{ fieldErrors.email }}</div>
              </div>

              <div class="col-md-6">
                <div class="field-header">
                  <label class="form-label fw-semibold text-title mb-0">{{ $t("customer_page.phone") }}</label>
                  <span class="field-caption">{{ $t("customer_page.phone_caption") }}</span>
                </div>
                <input
                  v-model="formData.phone"
                  type="text"
                  class="form-control modal-input"
                  :class="{ 'is-invalid': fieldErrors.phone }"
                  :placeholder="$t('customer_page.phone_placeholder')"
                />
                <div v-if="fieldErrors.phone" class="invalid-feedback">{{ fieldErrors.phone }}</div>
              </div>

              <div class="col-md-6">
                <div class="field-header">
                  <label class="form-label fw-semibold text-title mb-0">{{ $t("customer_page.country") }}</label>
                  <span class="field-caption">{{ $t("customer_page.country_caption") }}</span>
                </div>
                <input
                  v-model="formData.country"
                  type="text"
                  class="form-control modal-input"
                  :placeholder="$t('customer_page.country_value_placeholder')"
                />
              </div>

              <div class="col-md-6">
                <div class="field-header">
                  <label class="form-label fw-semibold text-title mb-0">{{ $t("customer_page.status") }}</label>
                  <span class="field-caption">{{ $t("customer_page.status_caption") }}</span>
                </div>
                <select v-model="formData.status" class="form-select modal-input">
                  <option :value="1">{{ $t("customer_page.status_active") }}</option>
                  <option :value="0">{{ $t("customer_page.status_inactive") }}</option>
                </select>
              </div>

              <div class="col-12">
                <div class="field-header">
                  <label class="form-label fw-semibold text-title mb-0">{{ $t("customer_page.address") }}</label>
                  <span class="field-caption">{{ $t("customer_page.address_caption") }}</span>
                </div>
                <textarea
                  v-model="formData.address"
                  rows="4"
                  class="form-control modal-input modal-textarea"
                  :placeholder="$t('customer_page.address_placeholder')"
                ></textarea>
              </div>
            </div>

            <div class="d-flex justify-content-between align-items-center mt-4 pt-2">
              <button type="button" class="btn btn-light px-4" data-bs-dismiss="modal">
                {{ $t("common.cancel") }}
              </button>
              <button type="submit" class="btn style-one px-4 h-55" :disabled="loading">
                {{ loading ? $t("customer_page.saving") : customer ? $t("customer_page.update_customer") : $t("customer_page.create_customer") }}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, nextTick, PropType, reactive, ref, watch } from "vue";
import Swal from "sweetalert2";
import { useI18n } from "vue-i18n";
import customerService, { type Customer } from "@/services/customer";
import stateStore from "@/utils/store";

export default defineComponent({
  name: "CreateCustomer",
  props: {
    customer: {
      type: Object as PropType<Customer | null>,
      default: null,
    },
  },
  emits: ["saved"],
  setup(props, { emit }) {
    const { t } = useI18n();
    const loading = ref(false);
    const fieldErrors = reactive<Record<string, string>>({});
    const formData = reactive<Customer>({
      code: "",
      name: "",
      email: "",
      phone: "",
      country: "",
      address: "",
      status: 1,
      type: "normal",
    });

    const resetForm = () => {
      formData.id = undefined;
      formData.code = "";
      formData.name = "";
      formData.email = "";
      formData.phone = "";
      formData.country = "";
      formData.address = "";
      formData.status = 1;
      formData.type = "normal";
    };

    watch(
      () => props.customer,
      (newCustomer) => {
        Object.keys(fieldErrors).forEach((key) => delete fieldErrors[key]);

        if (newCustomer) {
          Object.assign(formData, {
            ...newCustomer,
            status: newCustomer.status ?? 1,
            type: newCustomer.type || "normal",
          });
          return;
        }

        resetForm();
      },
      { immediate: true }
    );

    const hideModal = () => {
      const modalElement = document.getElementById("customerCreateModal");
      if (!modalElement) return;

      const dismissButton = modalElement.querySelector(
        '[data-bs-dismiss="modal"]'
      ) as HTMLButtonElement | null;
      if (dismissButton) {
        dismissButton.click();
      }

      const bootstrap = (window as {
        bootstrap?: {
          Modal?: {
            getInstance: (el: Element) => { hide: () => void } | null;
            getOrCreateInstance?: (el: Element) => { hide: () => void };
          };
        };
      }).bootstrap;
      const modalInstance =
        modalElement &&
        (bootstrap?.Modal?.getInstance(modalElement) ||
          bootstrap?.Modal?.getOrCreateInstance?.(modalElement));
      modalInstance?.hide();

      modalElement.classList.remove("show");
      modalElement.setAttribute("aria-hidden", "true");
      modalElement.removeAttribute("aria-modal");
      modalElement.removeAttribute("role");
      (modalElement as HTMLElement).style.display = "none";
      document.body.classList.remove("modal-open");
      document.body.style.removeProperty("padding-right");
      document.querySelectorAll(".modal-backdrop").forEach((backdrop) => backdrop.remove());
    };

    const handleSubmit = async () => {
      loading.value = true;
      Object.keys(fieldErrors).forEach((key) => delete fieldErrors[key]);

      try {
        if (props.customer?.id) {
          await customerService.updateCustomer(props.customer.id, formData);
        } else {
          await customerService.createCustomer(formData);
        }

        hideModal();
        resetForm();
        await nextTick();
        emit("saved");

        await Swal.fire({
          icon: "success",
          title: props.customer?.id ? t("customer_page.customer_updated") : t("customer_page.customer_created"),
          text: t("customer_page.customer_saved_successfully"),
          timer: 1500,
          showConfirmButton: false,
        });
      } catch (error: unknown) {
        console.error("Failed to save customer:", error);
        const axiosError = error as {
          response?: { data?: { errors?: Record<string, string[] | string>; message?: string } };
        };

        if (axiosError.response?.data?.errors) {
          const errors = axiosError.response.data.errors;
          Object.keys(errors).forEach((key) => {
            const fieldValue = errors[key];
            fieldErrors[key] = Array.isArray(fieldValue) ? String(fieldValue[0] || "") : String(fieldValue || "");
          });
        } else {
          await Swal.fire({
            icon: "error",
            title: t("customer_page.save_failed"),
            text: axiosError.response?.data?.message || t("customer_page.unable_to_save_customer"),
          });
        }
      } finally {
        loading.value = false;
      }
    };

    const generateCustomerCode = async () => {
      try {
        const customers = await customerService.getCustomers();
        const nextNumber =
          customers
            .map((item) => {
              const match = String(item.code || "").match(/(\d+)\s*$/);
              return match ? Number(match[1]) : 0;
            })
            .reduce((max, current) => Math.max(max, current), 0) + 1;

        const prefix = stateStore.referencePrefixes.customer || "CT-";
        formData.code = `${prefix}${String(nextNumber).padStart(3, "0")}`;
      } catch (error) {
        console.error("Failed to generate customer code:", error);
        await Swal.fire({
          icon: "error",
          title: t("customer_page.generate_failed"),
          text: t("customer_page.unable_to_generate_customer_code"),
        });
      }
    };

    return {
      loading,
      fieldErrors,
      formData,
      generateCustomerCode,
      handleSubmit,
    };
  },
});
</script>

<style scoped>
.modal-content {
  border-radius: 20px;
}

.modal-chip {
  display: inline-flex;
  align-items: center;
  padding: 8px 14px;
  border-radius: 999px;
  background: #edf2ff;
  color: #5d5fef;
  font-weight: 700;
  font-size: 13px;
}

.border-bottom-dashed {
  border-bottom: 1px dashed #e7ebf3;
}

.field-header {
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-bottom: 10px;
}

.field-caption {
  font-size: 12px;
  color: #98a2b3;
}

.reference-input-wrap {
  display: flex;
  align-items: stretch;
  gap: 10px;
}

.reference-input-wrap .form-control {
  flex: 1;
}

.modal-input {
  min-height: 52px;
  border: 1px solid #e6ebf2;
  border-radius: 12px;
  background: #fbfcfe;
  color: #1f2940;
  box-shadow: none;
  padding: 12px 16px;
}

.modal-input:focus {
  border-color: #5d5fef;
  background: #fff;
  box-shadow: 0 0 0 3px rgba(93, 95, 239, 0.12);
}

.modal-textarea {
  min-height: 140px;
  resize: vertical;
}

.generate-code-btn {
  border-radius: 12px;
  font-size: 13px;
  font-weight: 700;
  white-space: nowrap;
  min-height: 52px;
  padding-inline: 18px;
}
</style>
